/**
 * CR421 Phase 3B: Simplified Rule Packs Manager Dialog
 * Single activation model — no staging/publish split
 * Includes activation history tab and archived pack cleanup
 */

import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Settings2,
  Copy,
  Play,
  Archive,
  MoreHorizontal,
  Plus,
  Save,
  AlertTriangle,
  Clock,
  FileJson,
  Layers,
  GitMerge,
  Sparkles,
  XCircle,
  Trash2,
  Package,
  History,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { useRulePacks, RulePack, PackType, ActivationAuditEntry } from "@/hooks/useRulePacks";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RulePacksExplainer } from "./RulePacksExplainer";
import { ExampleJsonPicker } from "./ExampleJsonPicker";
import { PromptDetectionAlert, detectPromptText } from "./PromptDetectionAlert";

// ================================================================
// HELPERS
// ================================================================

function getStatusBadge(status: string, isActive: boolean) {
  if (isActive) {
    return <Badge className="bg-green-600 text-white">Active</Badge>;
  }
  switch (status) {
    case "DRAFT":
      return <Badge variant="outline">Draft</Badge>;
    case "ARCHIVED":
      return <Badge variant="secondary">Archived</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

// ================================================================
// PACK EDITOR
// ================================================================

interface PackEditorProps {
  pack: RulePack | null;
  packType: PackType;
  open: boolean;
  onClose: () => void;
  onSave: (name: string, version: string, config: Record<string, unknown>) => Promise<void>;
  saving: boolean;
}

function PackEditor({ pack, packType, open, onClose, onSave, saving }: PackEditorProps) {
  const [name, setName] = useState(pack?.name || "");
  const [version, setVersion] = useState(pack?.version || "v1.0.0");
  const [configText, setConfigText] = useState(
    pack?.config_json ? JSON.stringify(pack.config_json, null, 2) : "{}"
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [promptWarning, setPromptWarning] = useState<string | null>(null);

  const handleConfigChange = (value: string) => {
    setConfigText(value);
    setParseError(null);
    const warning = detectPromptText(value);
    setPromptWarning(warning);
  };

  const handleLoadExample = (config: Record<string, unknown>) => {
    setConfigText(JSON.stringify(config, null, 2));
    setParseError(null);
    setPromptWarning(null);
  };

  const handleSave = async () => {
    if (promptWarning) {
      setParseError("Cannot save: content appears to be a prompt, not JSON config.");
      return;
    }
    try {
      const config = JSON.parse(configText);
      setParseError(null);
      await onSave(name, version, config);
      onClose();
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Invalid JSON");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileJson className="h-5 w-5" />
            {pack ? `Edit ${packType} Pack` : `New ${packType} Pack`}
          </DialogTitle>
          <DialogDescription>
            {packType === "NORMALIZE" 
              ? "Configure field precedence, validation flags, and title formatting rules"
              : "Configure deduplication keys, similarity thresholds, and tie-breaker rules"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pack-name">Pack Name</Label>
              <Input id="pack-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Default Normalize Pack" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pack-version">Version</Label>
              <Input id="pack-version" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="e.g., v1.0.0" />
            </div>
          </div>

          <RulePacksExplainer />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="config-json" className="text-sm font-medium">Rule Pack Config (JSON only)</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Paste JSON config here. Prompts belong in Prompt Packs.</p>
              </div>
              <ExampleJsonPicker packType={packType} onSelect={handleLoadExample} />
            </div>
            <Textarea
              id="config-json"
              value={configText}
              onChange={(e) => handleConfigChange(e.target.value)}
              className={`font-mono text-sm min-h-[280px] ${promptWarning ? "border-destructive" : ""}`}
              placeholder='{"schema_version": "1.0.0", ...}'
            />
            {promptWarning && <PromptDetectionAlert warning={promptWarning} />}
            {parseError && !promptWarning && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <XCircle className="h-3 w-3" />
                {parseError}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !name || !!promptWarning}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Pack"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================================================================
// PACK LIST
// ================================================================

interface PackListProps {
  packs: RulePack[];
  packType: PackType;
  activePackId: string | null;
  onEdit: (pack: RulePack) => void;
  onClone: (pack: RulePack) => void;
  onActivate: (pack: RulePack) => void;
  onArchive: (pack: RulePack) => void;
  onDelete: (pack: RulePack) => void;
  saving: boolean;
  deletablePacks: Set<string>;
}

function PackList({
  packs,
  packType,
  activePackId,
  onEdit,
  onClone,
  onActivate,
  onArchive,
  onDelete,
  saving,
  deletablePacks,
}: PackListProps) {
  const [confirmActivate, setConfirmActivate] = useState<RulePack | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<RulePack | null>(null);

  return (
    <>
      <div className="space-y-2">
        {packs.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            No {packType.toLowerCase()} rule packs yet
          </p>
        ) : (
          packs.map((pack) => {
            const isActive = pack.id === activePackId;
            const isArchived = pack.status === "ARCHIVED";
            const isDeletable = deletablePacks.has(pack.id);

            return (
              <div
                key={pack.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  isActive 
                    ? "border-green-500/50 bg-green-500/5" 
                    : isArchived ? "opacity-60" : ""
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-xs ${packType === "NORMALIZE" ? "border-purple-500/50 text-purple-600 dark:text-purple-400" : "border-cyan-500/50 text-cyan-600 dark:text-cyan-400"}`}>
                      {packType === "NORMALIZE" ? "Normalize" : "Dedup"}
                    </Badge>
                    <span className="font-medium">{pack.name}</span>
                    <Badge variant="outline" className="text-xs">{pack.version}</Badge>
                    {getStatusBadge(pack.status, isActive)}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                    <Clock className="h-3 w-3" />
                    Updated {formatDistanceToNow(new Date(pack.updated_at), { addSuffix: true })}
                    {pack.updated_by && <span className="truncate max-w-[200px]">by {pack.updated_by}</span>}
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" disabled={saving}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(pack)}>
                      <Settings2 className="h-4 w-4 mr-2" />Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onClone(pack)}>
                      <Copy className="h-4 w-4 mr-2" />Clone
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {!isActive && !isArchived && (
                      <DropdownMenuItem onClick={() => setConfirmActivate(pack)}>
                        <Play className="h-4 w-4 mr-2" />Activate
                      </DropdownMenuItem>
                    )}
                    {!isActive && !isArchived && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onArchive(pack)} className="text-muted-foreground">
                          <Archive className="h-4 w-4 mr-2" />Archive
                        </DropdownMenuItem>
                        {isDeletable && (
                          <DropdownMenuItem onClick={() => setConfirmDelete(pack)} className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />Delete Permanently
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })
        )}
      </div>

      {/* Confirm Activation Dialog */}
      <AlertDialog open={!!confirmActivate} onOpenChange={(open) => !open && setConfirmActivate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate Rule Pack?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set <strong>{confirmActivate?.name}</strong> as the active {packType.toLowerCase()} pack for all runs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmActivate) { onActivate(confirmActivate); setConfirmActivate(null); } }}>
              Activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Delete Dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Rule Pack?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{confirmDelete?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirmDelete) { onDelete(confirmDelete); setConfirmDelete(null); } }} className="bg-destructive hover:bg-destructive/90">
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ================================================================
// ACTIVATION HISTORY
// ================================================================

function ActivationHistory() {
  const [history, setHistory] = useState<ActivationAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("rule_pack_activation_audit")
        .select("*")
        .order("activated_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      setHistory((data || []) as ActivationAuditEntry[]);
    } catch (err) {
      console.error("[ActivationHistory] Error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Skeleton className="h-32 w-full" />;

  if (history.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No activation history yet</p>;
  }

  return (
    <div className="space-y-2">
      {history.map((entry) => (
        <div key={entry.id} className="p-3 rounded-lg border text-sm space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={entry.pack_type === "NORMALIZE" ? "outline" : "secondary"} className="text-xs">
              {entry.pack_type}
            </Badge>
            <span className="text-muted-foreground">
              {format(new Date(entry.activated_at), "MMM d, yyyy HH:mm")}
            </span>
            {entry.activated_by && (
              <span className="text-muted-foreground truncate max-w-[150px]">by {entry.activated_by}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            {entry.previous_pack_id && (
              <span>{entry.previous_pack_id.slice(0, 8)}… → </span>
            )}
            <span className="text-foreground">{entry.active_pack_id.slice(0, 8)}…</span>
          </div>
          {entry.notes && <p className="text-xs text-muted-foreground italic">{entry.notes}</p>}
        </div>
      ))}
    </div>
  );
}

// ================================================================
// MAIN DIALOG
// ================================================================

interface RulePacksManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RulePacksManagerDialog({ open, onOpenChange }: RulePacksManagerDialogProps) {
  const { 
    data, loading, saving, refetch,
    createPack, updatePack, clonePack,
    activatePack, archivePack, deletePack,
    getActivePack,
  } = useRulePacks();

  const [editingPack, setEditingPack] = useState<{ pack: RulePack | null; type: PackType } | null>(null);
  const [deletableNormalize, setDeletableNormalize] = useState<Set<string>>(new Set());
  const [deletableDedupe, setDeletableDedupe] = useState<Set<string>>(new Set());
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    const checkDeletable = async () => {
      if (!data) return;
      const normalizeIds = new Set<string>();
      const dedupeIds = new Set<string>();

      for (const pack of data.normalize_packs) {
        try {
          const { data: result } = await supabase.functions.invoke(
            `rule-packs?pack_type=NORMALIZE&pack_id=${pack.id}&action=check_deletable`,
            { method: "GET" }
          );
          if (result?.deletable) normalizeIds.add(pack.id);
        } catch {
          // Ignore per-pack probe failures; pack stays non-deletable by default.
        }
      }

      for (const pack of data.dedupe_packs) {
        try {
          const { data: result } = await supabase.functions.invoke(
            `rule-packs?pack_type=DEDUPE&pack_id=${pack.id}&action=check_deletable`,
            { method: "GET" }
          );
          if (result?.deletable) dedupeIds.add(pack.id);
        } catch {
          // Ignore per-pack probe failures; pack stays non-deletable by default.
        }
      }

      setDeletableNormalize(normalizeIds);
      setDeletableDedupe(dedupeIds);
    };
    checkDeletable();
  }, [data]);

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const { error } = await supabase.functions.invoke("rule-packs?action=seed", {
        method: "POST", body: {},
      });
      if (error) throw error;
      toast.success("Default packs created and activated");
      refetch();
    } catch (err) {
      toast.error("Failed to create default packs");
    } finally {
      setSeeding(false);
    }
  };

  const handleCreateOrUpdate = async (name: string, version: string, config: Record<string, unknown>) => {
    if (!editingPack) return;
    if (editingPack.pack) {
      await updatePack(editingPack.type, editingPack.pack.id, { name, version, config_json: config });
    } else {
      await createPack(editingPack.type, name, version, config);
    }
  };

  const activeNormalize = getActivePack("NORMALIZE")?.id || null;
  const activeDedupe = getActivePack("DEDUPE")?.id || null;
  const hasMissingPacks = !activeNormalize || !activeDedupe;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Rule Packs Manager
          </SheetTitle>
          <SheetDescription>
            Manage Normalize and Dedup rule packs for batch run execution
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-hidden flex flex-col mt-6">
          {hasMissingPacks && !loading && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Missing Active Packs</AlertTitle>
              <AlertDescription className="flex items-center justify-between">
                <span>
                  {!activeNormalize && "Normalize pack missing. "}
                  {!activeDedupe && "Dedup pack missing. "}
                  Runs will fail without active packs.
                </span>
                <Button variant="outline" size="sm" onClick={handleSeedDefaults} disabled={seeding} className="ml-2">
                  <Sparkles className="h-4 w-4 mr-1" />
                  {seeding ? "Seeding..." : "Seed Defaults"}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          ) : (
            <Tabs defaultValue="normalize" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="normalize" className="gap-2">
                  <Layers className="h-4 w-4" />Normalize
                </TabsTrigger>
                <TabsTrigger value="dedupe" className="gap-2">
                  <GitMerge className="h-4 w-4" />Dedup
                </TabsTrigger>
                <TabsTrigger value="history" className="gap-2">
                  <History className="h-4 w-4" />History
                </TabsTrigger>
              </TabsList>

              <TabsContent value="normalize" className="flex-1 overflow-auto mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium">Normalize Rule Packs</h3>
                  <Button size="sm" onClick={() => setEditingPack({ pack: null, type: "NORMALIZE" })} disabled={saving}>
                    <Plus className="h-4 w-4 mr-1" />New Pack
                  </Button>
                </div>
                <ScrollArea className="h-[calc(100vh-400px)]">
                  <PackList
                    packs={data?.normalize_packs || []}
                    packType="NORMALIZE"
                    activePackId={activeNormalize}
                    onEdit={(pack) => setEditingPack({ pack, type: "NORMALIZE" })}
                    onClone={(pack) => clonePack("NORMALIZE", pack.id)}
                    onActivate={(pack) => activatePack("NORMALIZE", pack.id)}
                    onArchive={(pack) => archivePack("NORMALIZE", pack.id)}
                    onDelete={(pack) => deletePack("NORMALIZE", pack.id)}
                    saving={saving}
                    deletablePacks={deletableNormalize}
                  />
                </ScrollArea>
              </TabsContent>

              <TabsContent value="dedupe" className="flex-1 overflow-auto mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium">Dedup Rule Packs</h3>
                  <Button size="sm" onClick={() => setEditingPack({ pack: null, type: "DEDUPE" })} disabled={saving}>
                    <Plus className="h-4 w-4 mr-1" />New Pack
                  </Button>
                </div>
                <ScrollArea className="h-[calc(100vh-400px)]">
                  <PackList
                    packs={data?.dedupe_packs || []}
                    packType="DEDUPE"
                    activePackId={activeDedupe}
                    onEdit={(pack) => setEditingPack({ pack, type: "DEDUPE" })}
                    onClone={(pack) => clonePack("DEDUPE", pack.id)}
                    onActivate={(pack) => activatePack("DEDUPE", pack.id)}
                    onArchive={(pack) => archivePack("DEDUPE", pack.id)}
                    onDelete={(pack) => deletePack("DEDUPE", pack.id)}
                    saving={saving}
                    deletablePacks={deletableDedupe}
                  />
                </ScrollArea>
              </TabsContent>

              <TabsContent value="history" className="flex-1 overflow-auto mt-4">
                <h3 className="text-sm font-medium mb-4">Activation History</h3>
                <ScrollArea className="h-[calc(100vh-400px)]">
                  <ActivationHistory />
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </div>

        {editingPack && (
          <PackEditor
            pack={editingPack.pack}
            packType={editingPack.type}
            open={true}
            onClose={() => setEditingPack(null)}
            onSave={handleCreateOrUpdate}
            saving={saving}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
